import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useEffect, useState, type ReactNode } from "react";

export type LegalDocumentKind = "terms" | "privacy";

const legalDocuments: Record<LegalDocumentKind, { title: string; path: string }> = {
  terms: {
    title: "Termos de uso",
    path: "/legal/termos-de-uso.md"
  },
  privacy: {
    title: "Política de uso e privacidade",
    path: "/legal/politica-de-privacidade.md"
  }
};

function renderInline(text: string) {
  const tokenPattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)]+\)|https?:\/\/[^\s)]+|`[^`]+`)/g;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(text))) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }

    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push(
        <Box component="strong" key={`${match.index}-strong`} sx={{ color: "text.primary", fontWeight: 800 }}>
          {token.slice(2, -2)}
        </Box>
      );
    } else if (token.startsWith("[")) {
      const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
      if (linkMatch) {
        nodes.push(
          <Link
            key={`${match.index}-markdown-link`}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer"
            underline="hover"
          >
            {linkMatch[1]}
          </Link>
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith("`")) {
      nodes.push(
        <Box
          component="code"
          key={`${match.index}-code`}
          sx={{ px: 0.5, borderRadius: 0.5, bgcolor: "action.hover", color: "text.primary" }}
        >
          {token.slice(1, -1)}
        </Box>
      );
    } else {
      const punctuation = token.match(/[.,;:!?]+$/)?.[0] ?? "";
      const url = punctuation ? token.slice(0, -punctuation.length) : token;
      nodes.push(
        <span key={`${match.index}-url`}>
          <Link href={url} target="_blank" rel="noreferrer" underline="hover">
            {url}
          </Link>
          {punctuation}
        </span>
      );
    }

    cursor = match.index + token.length;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
}

function renderMarkdown(content: string) {
  return content
    .replace(/\r/g, "")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, index) => {
      const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
      const heading = lines.length === 1 ? lines[0]?.match(/^(#{1,3})\s+(.+)$/) : null;
      const unorderedList = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
      const orderedList = lines.length > 0 && lines.every((line) => /^\d+[.)]\s+/.test(line));

      if (heading) {
        const level = heading[1]?.length ?? 1;
        return (
          <Typography
            key={index}
            variant={level === 1 ? "h2" : "h3"}
            sx={{
              mt: level === 1 ? 1 : 0.75,
              color: "text.primary",
              fontSize: level === 1 ? "1.45rem" : level === 2 ? "1.08rem" : "0.98rem",
              lineHeight: 1.3
            }}
          >
            {renderInline(heading[2] ?? "")}
          </Typography>
        );
      }

      if (unorderedList || orderedList) {
        const listItems = lines.map((line) => line.replace(unorderedList ? /^[-*]\s+/ : /^\d+[.)]\s+/, ""));
        return (
          <Box
            key={index}
            component={unorderedList ? "ul" : "ol"}
            sx={{ m: 0, pl: 3, color: "text.secondary", "& li": { pl: 0.75, mb: 0.9 } }}
          >
            {listItems.map((item, itemIndex) => (
              <Box component="li" key={`${index}-${itemIndex}`}>
                <Typography component="span" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                  {renderInline(item)}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }

      return (
        <Typography key={index} color="text.secondary" sx={{ lineHeight: 1.8 }}>
          {lines.map((line, lineIndex) => (
            <span key={`${index}-${lineIndex}`}>
              {lineIndex ? <br /> : null}
              {renderInline(line)}
            </span>
          ))}
        </Typography>
      );
    });
}

export function LegalDocumentButton({
  children,
  documentKind,
  onOpen
}: {
  children: ReactNode;
  documentKind: LegalDocumentKind;
  onOpen: (documentKind: LegalDocumentKind) => void;
}) {
  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen(documentKind);
      }}
      sx={{ fontWeight: 800, verticalAlign: "baseline" }}
    >
      {children}
    </Link>
  );
}

export function LegalDocumentDialog({
  documentKind,
  onClose
}: {
  documentKind: LegalDocumentKind | null;
  onClose: () => void;
}) {
  const theme = useTheme();
  const [documentState, setDocumentState] = useState<{
    path: string | null;
    content: string;
    error: string | null;
  }>({
    path: null,
    content: "",
    error: null
  });
  const documentConfig = documentKind ? legalDocuments[documentKind] : null;
  const loading = Boolean(documentConfig && documentState.path !== documentConfig.path);

  useEffect(() => {
    if (!documentConfig) {
      return;
    }

    let active = true;
    const path = documentConfig.path;

    void fetch(path)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Documento indisponível.");
        }

        return response.text();
      })
      .then((text) => {
        if (active) {
          setDocumentState({
            path,
            content: text,
            error: null
          });
        }
      })
      .catch(() => {
        if (active) {
          setDocumentState({
            path,
            content: "",
            error: "Não foi possível carregar este documento agora."
          });
        }
      });

    return () => {
      active = false;
    };
  }, [documentConfig]);

  return (
    <Dialog
      open={Boolean(documentConfig)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      transitionDuration={320}
      slotProps={{
        paper: {
          variant: "outlined",
          sx: {
            overflow: "hidden",
            boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.28)}`
          }
        }
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Stack spacing={0.5}>
          <Typography variant="h2">{documentConfig?.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Documento institucional da GeoAnalytics.
          </Typography>
        </Stack>
        <IconButton
          aria-label="Fechar documento"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            color: "text.primary",
            "&:hover": {
              bgcolor: "action.hover",
              borderColor: "primary.main"
            }
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ maxHeight: "62vh", overflow: "auto", p: { xs: 2.25, sm: 3 } }}>
          {loading ? (
            <Stack spacing={1.5} sx={{ alignItems: "center", py: 4 }}>
              <CircularProgress size={28} />
              <Typography color="text.secondary">Carregando documento...</Typography>
            </Stack>
          ) : documentState.error ? (
            <Alert severity="warning">{documentState.error}</Alert>
          ) : (
            <Stack spacing={2}>{renderMarkdown(documentState.content)}</Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
}
